import { Controller } from "@hotwired/stimulus";

// How much air to leave between the widened panel and the window edges.
const WIDE_GUTTER = 16;
// Below this much reclaimed width, widening isn't worth offering.
const MIN_WIDE_GAIN = 24;

// Two size modes for the dashboard panel, exclusive of each other: "wide" keeps
// the panel in the page but fills the content column, "fullscreen" pins it to
// the viewport. Esc leaves full screen.
//
// Not the Fullscreen API: the Reveal SDK renders some dialogs into
// document.body, which a fullscreen element would hide.
export default class extends Controller {
  static targets = ["wideButton", "fullscreenButton"];

  connect() {
    this.mode = "normal";
    this.handleKeydown = (event) => {
      if (event.key === "Escape" && this.mode === "fullscreen") this.apply("normal");
    };
    // Only re-measures — must not call apply(), which dispatches its own resize.
    this.handleResize = () => this.syncWide();
    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("resize", this.handleResize);
    this.syncWide();
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResize);
    document.body.classList.remove("reveal-embed-expanded");
  }

  toggleWide(event) {
    event?.preventDefault();
    this.apply(this.mode === "wide" ? "normal" : "wide");
  }

  toggleFullscreen(event) {
    event?.preventDefault();
    this.apply(this.mode === "fullscreen" ? "normal" : "fullscreen");
  }

  apply(mode) {
    this.mode = mode;
    const fullscreen = mode === "fullscreen";

    this.element.classList.toggle("reveal-embed-panel--wide", mode === "wide");
    this.element.classList.toggle("reveal-embed-panel--fullscreen", fullscreen);
    document.body.classList.toggle("reveal-embed-expanded", fullscreen);
    this.syncWide();

    if (this.hasWideButtonTarget) this.syncButton(this.wideButtonTarget, mode === "wide");
    if (this.hasFullscreenButtonTarget) {
      this.syncButton(this.fullscreenButtonTarget, fullscreen);
      this.fullscreenButtonTarget.title = fullscreen ? "Exit full screen (Esc)" : "Fill the browser window";
    }
    // RevealView only re-measures on window resize; a CSS class flip fires nothing.
    window.dispatchEvent(new Event("resize"));
  }

  // The page's content column — <main> minus its own padding, held off the
  // window edges. Deliberately stops at the column rather than the viewport:
  // the backoffice's <main> sits beside a fixed sidebar, and a viewport-wide
  // panel would slide underneath it.
  contentBounds() {
    const main = this.element.closest("main") || document.body;
    const rect = main.getBoundingClientRect();
    const style = getComputedStyle(main);

    return {
      left: Math.max(rect.left + parseFloat(style.paddingLeft), WIDE_GUTTER),
      right: Math.min(rect.right - parseFloat(style.paddingRight), document.documentElement.clientWidth - WIDE_GUTTER),
    };
  }

  // Measures against the panel's untouched geometry — clearing the offset first
  // is what makes this safe to re-run on resize. Surfaces whose content column
  // already fills the page have nothing to claim, so the control is disabled
  // there instead of moving the panel by a pixel.
  syncWide() {
    const { style } = this.element;
    style.removeProperty("--reveal-embed-wide-offset");
    style.removeProperty("--reveal-embed-wide-width");

    // Full screen lifts the panel out of the flow, so there is no page geometry
    // to judge — keep the last verdict so the control stays switchable.
    if (this.mode === "fullscreen") return;

    const { left, right } = this.contentBounds();
    const natural = this.element.getBoundingClientRect();
    const roomToGrow = right - left - natural.width >= MIN_WIDE_GAIN;

    if (this.hasWideButtonTarget) {
      this.wideButtonTarget.disabled = !roomToGrow;
      this.wideButtonTarget.title = roomToGrow ? "Fill the page width" : "The dashboard already fills this page";
    }
    if (!roomToGrow || this.mode !== "wide") return;

    style.setProperty("--reveal-embed-wide-offset", `${left - natural.left}px`);
    style.setProperty("--reveal-embed-wide-width", `${right - left}px`);
  }

  syncButton(button, active) {
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}
